import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const location = useLocation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的提交，我们会尽快处理！');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotField && honeypotField.value.trim() === '') {
        formData.delete('phone_alt');
      }
      const response = await fetch('https://readdy.ai/api/form/d9u09qt3pcjqs2fcsjv0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const code = (parsed as { code?: string }).code;
      if (response.ok && code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的提交！我们会尽快处理您的信息。');
        form.reset();
      } else {
        setFormStatus('error');
        const serverMsg = (parsed as { meta?: { message?: string } }).meta?.message
          || (parsed as { message?: string }).message
          || '提交失败，请稍后重试';
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <section className="w-full px-4 md:px-6 py-12 bg-background-50">
          <div className="max-w-[800px] mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">关于我们</span>
            </nav>

            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-8">
              关于我们
            </h1>

            <div className="prose max-w-none space-y-8">
              <div>
                <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">品牌介绍</h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-3">
                  <strong>一级免费网站免费资源导航平台</strong>创立于2025年，是一支热爱互联网、热爱分享的团队打造的专业免费资源导航平台。我们深知在信息爆炸的时代，找到真正优质、真正免费的资源有多难——太多打着"免费"旗号的网站，点进去才发现处处是付费陷阱。
                </p>
                <p className="text-foreground-600 text-sm leading-relaxed mb-3">
                  我们的使命很简单：<strong>为每一位互联网用户提供真正免费、高质量的网络资源导航服务</strong>。无论是设计师需要的素材，开发者需要的工具，还是学生需要的课程，我们都希望通过精心的筛选和整理，帮助你节省搜索时间，快速找到最适合的免费资源。
                </p>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  目前，平台已收录<strong>超过500个优质免费资源</strong>，覆盖设计素材、图片视频、开发工具、在线工具、学习资源、字体下载、效率办公、AI工具等12个核心分类，每月服务超过50,000名用户，且这个数字还在持续增长。
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">我们的原则</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-shield-check-line', title: '真实免费', desc: '只收录经过验证的免费资源，绝不以免费之名行收费之实' },
                    { icon: 'ri-eye-line', title: '严格审核', desc: '每个资源都经过人工审核，确保质量和安全性' },
                    { icon: 'ri-refresh-line', title: '持续更新', desc: '每日关注最新资源动态，及时更新和清理失效链接' },
                    { icon: 'ri-user-heart-line', title: '用户至上', desc: '无需注册，无需付费，简洁高效的浏览体验' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-3 bg-background-50 border border-background-200/70 rounded-xl p-4">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-lg text-primary-600`}></i>
                      </div>
                      <div>
                        <h4 className="text-foreground-900 font-semibold text-sm mb-1">{item.title}</h4>
                        <p className="text-foreground-500 text-xs leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">用户口碑</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { name: '张同学', role: '自由设计师', text: '作为一个独立设计师，预算有限，一级免费帮我找到了好多高质量的免费素材，太实用了！', rating: 5 },
                    { name: '李工程师', role: '全栈开发者', text: '开发工具分类整理得很棒，找到了好几个之前不知道的免费API和工具，效率提升不少。', rating: 5 },
                    { name: '王老师', role: '在线教育从业者', text: '课程资源板块非常适合我，推荐的免费学习平台质量很高，已经推荐给了我的学生们。', rating: 5 },
                    { name: '赵同学', role: '在校大学生', text: '作为一个学生，免费资源对我来说太重要了。这个网站分类清晰，找资源特别方便。', rating: 5 },
                    { name: '陈经理', role: '初创公司CEO', text: '创业初期每一分钱都要花在刀刃上，这里推荐的免费工具帮我们节省了大量成本。', rating: 4 },
                    { name: '刘编辑', role: '自媒体创作者', text: '图片素材和字体资源太全了，做内容再也不担心版权问题了，强烈推荐！', rating: 5 },
                  ].map((review, idx) => (
                    <div key={idx} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-xs ${i < review.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-foreground-600 text-xs leading-relaxed mb-4">
                        "{review.text}"
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-700 text-xs font-semibold">{review.name[0]}</span>
                        </div>
                        <div>
                          <div className="text-foreground-800 text-xs font-semibold">{review.name}</div>
                          <div className="text-foreground-400 text-[11px]">{review.role}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">联系我们</h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                  如果您有优质免费资源推荐、网站建议或合作意向，欢迎通过下方表单与我们取得联系。
                  我们会在24小时内回复您的消息。
                </p>

                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-foreground-700 text-xs font-medium mb-1.5">
                        您的姓名 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-foreground-700 text-xs font-medium mb-1.5">
                        电子邮箱 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-foreground-700 text-xs font-medium mb-1.5">
                      主题
                    </label>
                    <select
                      id="contact-subject"
                      name="subject"
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                    >
                      <option value="">请选择主题</option>
                      <option value="resource-recommend">资源推荐</option>
                      <option value="cooperation">商务合作</option>
                      <option value="feedback">意见反馈</option>
                      <option value="bug-report">问题报告</option>
                      <option value="other">其他</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-foreground-700 text-xs font-medium mb-1.5">
                      详细描述 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请详细描述您的需求或建议（最多500字）"
                    ></textarea>
                    <p className="text-foreground-400 text-[11px] mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="spam-guard"
                  />

                  <div className="flex items-start gap-4">
                    <button
                      type="submit"
                      className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
                    >
                      提交信息
                    </button>
                    {formStatus !== 'idle' && (
                      <div className={`flex items-center gap-2 py-3 ${
                        formStatus === 'success' ? 'text-accent-700' : 'text-red-500'
                      } text-sm`}>
                        <i className={formStatus === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                        {formMessage}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}